import _ from 'lodash';
import { ProductTypes } from '../types';
import { standardQuerySerializer } from '../api/serializers';
import { applyQueryConfig, makeCancelable, ServiceHandler } from '../utils';
import { quoteServiceMappings } from './quoteServiceMappings';
import { quoteServiceMappingsE7 } from './quoteServiceMappingsE7';

const quoteParamsSerializer = (params: any) =>
  standardQuerySerializer(applyQueryConfig(quoteServiceMappings, params));

const fetchQuotesHandler = makeCancelable(
  ServiceHandler({
    url: '/api/quote/v1/search',
    paramsSerializer: quoteParamsSerializer,
  })
);
/** Pass `signal` to run alongside other quote searches instead of aborting the one before it. */
export const fetchQuotes = (
  params: Record<string, unknown> = {},
  { signal }: { readonly signal?: AbortSignal } = {}
) => fetchQuotesHandler({ params, signal });

const fetchBindersHandler = makeCancelable(
  ServiceHandler({
    url: '/api/policy/v1/search/binders',
    paramsSerializer: quoteParamsSerializer,
  })
);
export const fetchBinders = (params: Record<string, unknown> = {}) =>
  fetchBindersHandler({ params });

const getBinderStatusOptionsHandler = ServiceHandler({
  url: '/api/quote/v1/binder-statuses',
});
export const getBinderStatusOptions = (params: Record<string, unknown> = {}) =>
  getBinderStatusOptionsHandler({ params });

const fetchUwQuotesHandler = makeCancelable(
  ServiceHandler({
    url: '/api/quote/v1/search/uw-inbox',
    paramsSerializer: quoteParamsSerializer,
  })
);
export const fetchUwQuotes = (params: Record<string, unknown> = {}) =>
  fetchUwQuotesHandler({ params });

const fetchUwReferalReasonHandler = makeCancelable(
  ServiceHandler({
    url: '/api/account/v1/account/referral-reasons',
    paramsSerializer: quoteParamsSerializer,
  })
);
export const fetchUwReferalReason = (params: Record<string, unknown> = {}) =>
  fetchUwReferalReasonHandler({ params });

const binderReadHandler = makeCancelable(
  ServiceHandler({
    url: '/api/policy/v1/search/binders/read',
    paramsSerializer: quoteParamsSerializer,
  })
);
export const binderRead = (params: Record<string, unknown> = {}) =>
  binderReadHandler({ params });

const updateQuoteAgentInfoHandler = makeCancelable(
  ServiceHandler({ url: '/api/quote/v1/action/agent', method: 'put' })
);
export const updateQuoteAgentInfo = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => updateQuoteAgentInfoHandler({ data });

const getExpiredQuotesHandler = makeCancelable(
  ServiceHandler({
    url: '/api/quote/v1/search/action?filter%5BagencyStatus%5D%5Bin%5D=READY%2CBOUND%2CINDICATION',
    paramsSerializer: quoteParamsSerializer,
  })
);
export const getExpiredQuotes = (params: Record<string, unknown> = {}) =>
  getExpiredQuotesHandler({ params });

const fetchQuotesStatsHandler = ServiceHandler({
  url: '/api/quote/v1/search/premium',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchQuotesStats = (params: Record<string, unknown> = {}) =>
  fetchQuotesStatsHandler({ params });

const fetchBinderQuotesStatsHandler = ServiceHandler({
  url: '/api/quote/v1/search/bound-premium',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchBinderQuotesStats = (params: Record<string, unknown> = {}) =>
  fetchBinderQuotesStatsHandler({ params });

const fetchUWInboxStatsHandler = ServiceHandler({
  url: '/api/quote/v1/search/uw-inbox/aggregate',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchUWInboxStats = (params: Record<string, unknown> = {}) =>
  fetchUWInboxStatsHandler({ params });

/**
 * New location src/api/accounts.api.ts
 * @deprecated
 */
const fetchUniqueAccountStatsHandler = ServiceHandler({
  url: '/api/quote/v1/search/uniqueAccounts',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchUniqueAccountStats = (params: Record<string, unknown> = {}) =>
  fetchUniqueAccountStatsHandler({ params });

const fetchAggregateQuotesHandler = ServiceHandler({
  url: '/api/quote/v1/search/aggregateQuotes',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchAggregateQuotes = (params: Record<string, unknown> = {}) =>
  fetchAggregateQuotesHandler({ params });

const fetchAggregateQuoteAccountsHandler = ServiceHandler({
  url: '/api/quote/v1/search/aggregateQuotedAccounts',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchAggregateQuoteAccounts = (
  params: Record<string, unknown> = {}
) => fetchAggregateQuoteAccountsHandler({ params });

const generateExcessPolicyHandler = ServiceHandler({
  url: '/api/policy/v1/P250/excess/issue/:policyId',
  method: 'put',
});
export function generateExcessPolicy(policyId: any) {
  return generateExcessPolicyHandler(policyId);
}

const regenerateApplicationP100Handler = ServiceHandler({
  url: '/api/quote/v1/document/:id',
});
const regenerateApplicationP250Handler = ServiceHandler({
  url: '/api/quote/v1/quoteDoc',
  method: 'put',
});
const regenerateApplicationPxHandler = ServiceHandler({
  url: 'api/quote/v1/:product/:id/quoteDoc',
  method: 'put',
});
export function regenerateApplication({ id, product }: any) {
  if (product === ProductTypes.p100) {
    return regenerateApplicationP100Handler(id);
  }

  if (product === ProductTypes.p250) {
    return regenerateApplicationP250Handler({ params: { quoteId: id } });
  }

  return regenerateApplicationPxHandler(product, id);
}

const regenerateProposalHandler = ServiceHandler({
  url: '/api/quote/v1/proposal',
  method: 'put',
});
export function regenerateProposal({ id, product }: any) {
  return regenerateProposalHandler({ params: { quoteId: id, product } });
}

const generatePolicyQuoteP100Handler = ServiceHandler({
  url: '/api/quote/v1/action/policy/:quoteId',
  method: 'put',
});
const generatePolicyQuoteSignedHandler = ServiceHandler({
  url: '/api/esign/v1/quoteSigned',
  method: 'put',
});
export function generatePolicyQuote(quoteId: any, product: any) {
  if (product === ProductTypes.p100) {
    return generatePolicyQuoteP100Handler(quoteId);
  }

  return generatePolicyQuoteSignedHandler({ params: { quoteId } });
}

/**
 * New location src/apis/quotes.api.ts
 * @deprecated
 */
const fetchQuoteDetailsHandler = ServiceHandler({
  url: '/api/quote/v1/:quoteId',
});
export function fetchQuoteDetails(quoteId: any) {
  return fetchQuoteDetailsHandler(quoteId);
}

/**
 * New location src/apis/quotes.api.ts
 * @deprecated
 */
const fetchP250QuoteDetailsHandler = ServiceHandler({
  url: '/api/quote/v1/P250/quotes/:quoteId',
});
export function fetchP250QuoteDetails(quoteId: any) {
  return fetchP250QuoteDetailsHandler(quoteId);
}

export function fetchQuoteDetailsOffProduct(product: any, quoteId: any) {
  if (product === 'P250') {
    return fetchP250QuoteDetails(quoteId);
  }
  return fetchQuoteDetails(quoteId);
}

const fetchP250QuotePageDetailsHandler = ServiceHandler({
  url: '/api/quote/v1/P250/details/:quoteId',
});
export function fetchP250QuotePageDetails(quoteId: any) {
  return fetchP250QuotePageDetailsHandler(quoteId);
}

const fetchPolicyQuoteDetailsHandler = ServiceHandler({
  url: '/api/quote/v1/policy/:quoteId',
});
export function fetchPolicyQuoteDetails(quoteId: any) {
  return fetchPolicyQuoteDetailsHandler(quoteId);
}

const fetchUnreadQuotesHandler = ServiceHandler({
  url: '/api/quote/v1/search/read',
  paramsSerializer: quoteParamsSerializer,
});
export const fetchUnreadQuotes = (params: Record<string, unknown> = {}) =>
  fetchUnreadQuotesHandler({ params });

function cleanAis(payload: any) {
  return {
    ...payload,
    additionalInsureds: payload.additionalInsureds.map(
      ({ naicsCode, ...ai }: any) => {
        const naicsDescription = getNaicsDescription({ naicsCode, ...ai });
        return {
          ...ai,
          naicsCode:
            typeof naicsCode === 'object' ? naicsCode.value : naicsCode,
          naicsDescription,
        };
      }
    ),
  };
}

const getNaicsDescription = (ai: any) => {
  const { naicsCode, naicsDescription } = ai;
  let description;
  if (typeof naicsDescription === 'string') {
    description = naicsDescription;
  }
  if (typeof naicsDescription === 'object') {
    description = _.get(
      naicsDescription,
      'meta.secondaryIndustry',
      description
    );
  }
  if (typeof naicsCode === 'object') {
    description = _.get(naicsCode, 'meta.secondaryIndustry', description);
  }
  return description;
};

const generateQuoteForAccountHandler = ServiceHandler({
  url: '/api/quote/v1/:accountId',
  method: 'post',
});
export function generateQuoteForAccount(accountId: any, details: any) {
  const payload = cleanAis(details);
  return generateQuoteForAccountHandler(accountId, { data: payload });
}

const generateQuoteEditHandler = ServiceHandler({
  url: 'api/quote/v1/edit/:quoteId',
  method: 'put',
});
export function generateQuoteEdit(quoteId: any, details: any) {
  const payload = cleanAis(details);
  return generateQuoteEditHandler(quoteId, { data: payload });
}

const updateBrokerFeeHandler = ServiceHandler({
  url: '/api/quote/v1/broker-fees/:quoteId',
  method: 'put',
});
export function updateBrokerFee(quoteId: any, details: any) {
  return updateBrokerFeeHandler(quoteId, { data: details });
}

const updateMgaFeeHandler = ServiceHandler({
  url: '/api/quote/v1/mga-fees/:quoteId',
  method: 'put',
});
export function updateMgaFee(quoteId: any, details: any) {
  return updateMgaFeeHandler(quoteId, { data: details });
}

const generateMultipleQuotesForAccountHandler = ServiceHandler({
  url: '/api/quote/v1/bulk',
  method: 'post',
});
export function generateMultipleQuotesForAccount(details: any) {
  return generateMultipleQuotesForAccountHandler({ data: details });
}

const addToListQuotesHandler = ServiceHandler({
  url: '/api/quote/v1/calculator',
  method: 'post',
});
export function addToListQuotes(details: any) {
  return addToListQuotesHandler({ data: details });
}

const getListForAccountHandler = ServiceHandler({
  url: '/api/quote/v1/calculator/account/:accountId',
});
export function getListForAccount(accountId: any, agencyId: any) {
  return getListForAccountHandler(accountId, { params: { agencyId } });
}

const sendQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/action/send',
  method: 'put',
});
export function sendQuote(details: any) {
  return sendQuoteHandler({ data: details });
}

const downloadQuoteProposalHandler = ServiceHandler({
  url: '/api/docs/v1/quote/:accountId/proposal/:quoteId',
});
export function downloadQuoteProposal(
  accountId: any,
  quoteId: any,
  created: any
) {
  return downloadQuoteProposalHandler(accountId, quoteId, {
    params: { createdDate: created },
    responseType: 'json',
  });
}

const restoreQuotesHandler = makeCancelable(
  ServiceHandler({ url: '/api/quote/v1/search/restore', method: 'put' })
);
export const restoreQuotes = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => restoreQuotesHandler({ data });

const downloadQuoteDocumentsHandler = ServiceHandler({
  url: '/api/docs/v1/quote/:accountId/:quoteId',
});
export function downloadQuoteDocuments(
  accountId: any,
  quoteId: any,
  created: any,
  isEndorsement: any,
  product: any,
  quoteNumber: any
) {
  return downloadQuoteDocumentsHandler(accountId, quoteId, {
    params: { createdDate: created, isEndorsement, product, quoteNumber },
  });
}

const deleteQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/:quoteId',
  method: 'delete',
});
export function deleteQuote(quoteId: any) {
  return deleteQuoteHandler(quoteId);
}

export function deleteBulkQuote(details: any) {
  // This endpoint was previously for SalesInbox, but is now deprecated and removed.
  throw new Error('Bulk quote deletion via SalesInbox is no longer supported.');
}

const deleteListedQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/calculator/:quoteId',
  method: 'delete',
});
export function deleteListedQuote(quoteId: any, agencyId: any) {
  return deleteListedQuoteHandler(quoteId, { params: { agencyId } });
}

const deleteListedQuotesAllHandler = ServiceHandler({
  url: '/api/quote/v1/calculator/',
  method: 'put',
});
export function deleteListedQuotesAll(details: any) {
  return deleteListedQuotesAllHandler({ data: details });
}

/**
 * New bind api found in src/api/bind-quote.api.ts
 * @deprecated
 */
const approveQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/action/approve',
  method: 'put',
});
export function approveQuote(details: any) {
  return approveQuoteHandler({ data: details });
}

const updateQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/update',
  method: 'put',
});
export function updateQuote(details: any) {
  return updateQuoteHandler({ data: details });
}

const getQuoteStatusHandler = ServiceHandler({
  url: '/api/quote/v1/status/:quoteId',
});
export function getQuoteStatus(quoteId: any) {
  return getQuoteStatusHandler(quoteId);
}

const deleteALlBoundQuotesHandler = ServiceHandler({
  url: 'api/quote/v1/bulk/bound',
  method: 'delete',
});
export function deleteALlBoundQuotes() {
  return deleteALlBoundQuotesHandler();
}

const deleteALlInvalidQuotesHandler = ServiceHandler({
  url: 'api/quote/v1/bulk/invalid',
  method: 'delete',
});
export function deleteALlInvalidQuotes() {
  return deleteALlInvalidQuotesHandler();
}

const retryRequestQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/action/retryrequest/:quoteId',
  method: 'put',
});
export function retryRequestQuote(quoteId: any) {
  return retryRequestQuoteHandler(quoteId);
}

const deleteALlExpiredQuotesHandler = ServiceHandler({
  url: '/api/quote/v1/bulk/expired',
  method: 'delete',
});
export function deleteALlExpiredQuotes() {
  return deleteALlExpiredQuotesHandler();
}

const actionOnCustomerHandler = ServiceHandler({
  url: 'api/quote/v1/action/customer',
  method: 'put',
});
export const actionOnCustomer = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => actionOnCustomerHandler({ data });

const attestTermsOnQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/attest/:quoteId',
  method: 'put',
});
export const attestTermsOnQuote = (
  { quoteId }: { quoteId: string | number },
  data: unknown = {}
) => attestTermsOnQuoteHandler(quoteId, { data });

const sendSignRequestHandler = ServiceHandler({
  url: '/api/quote/v1/action/send-sign-request',
  method: 'post',
});
export const sendSignRequest = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => sendSignRequestHandler({ data });

export const getQuote = (quoteId: any, productType: any) => {
  if (productType === ProductTypes.p100) {
    return fetchQuoteDetails(quoteId);
  }
  if (productType === ProductTypes.p250) {
    return fetchP250QuotePageDetails(quoteId);
  }
};

const sendRenewalQuoteDetailsEmailHandler = ServiceHandler({
  url: '/api/quote/v1/action/renewal',
  method: 'put',
});
export const sendRenewalQuoteDetailsEmail = (
  params: Record<string, unknown> = {},
  data?: unknown
) => sendRenewalQuoteDetailsEmailHandler({ params, data });

const getAgencyStatusesHandler = ServiceHandler({
  url: '/api/quote/v1/agency-statuses',
});
export const getAgencyStatuses = (params: Record<string, unknown> = {}) =>
  getAgencyStatusesHandler({ params });

const getCustomerStatusesHandler = ServiceHandler({
  url: '/api/quote/v1/customer-statuses',
});
export const getCustomerStatuses = (params: Record<string, unknown> = {}) =>
  getCustomerStatusesHandler({ params });

export const getMarketTypes = ServiceHandler({
  url: '/api/quote/v1/market-types',
});

const getEndorsementsInProgressHandler = ServiceHandler({
  url: `/api/quote/v1/endorsement/:product/available/:policyNumber`,
});
export const getEndorsementsInProgress = ({ product, policyNumber }: any) =>
  getEndorsementsInProgressHandler(product, policyNumber);

const deleteEndorsementsInProgressHandler = ServiceHandler({
  url: '/api/quote/v1/endorsement/:product/:policyNumber',
  method: 'delete',
});
export const deleteEndorsementsInProgress = ({ product, policyNumber }: any) =>
  deleteEndorsementsInProgressHandler(product, policyNumber);

const generateEndorsementQuoteHandler = ServiceHandler({
  url: '/api/quote/v1/endorsement',
  method: 'post',
});
export const generateEndorsementQuote = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => generateEndorsementQuoteHandler({ data });

const generateEndorsementQuoteP250Handler = ServiceHandler({
  url: '/api/quote/v1/endorsement/P250/:id',
  method: 'post',
});
export const generateEndorsementQuoteP250 = (
  { id }: { id: string | number },
  data?: unknown
) => generateEndorsementQuoteP250Handler(id, { data });

const createOverrideHandler = ServiceHandler({
  url: '/api/quote/v1/tam/override',
  method: 'post',
});
export const createOverride = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => createOverrideHandler({ data });

const deleteOverrideHandler = ServiceHandler({
  url: '/api/quote/v1/tam/override',
  method: 'delete',
});
export const deleteOverride = (
  _params: Record<string, unknown> = {},
  data?: unknown
) => deleteOverrideHandler({ data });

const fetchOverridesHandler = makeCancelable(
  ServiceHandler({
    url: '/api/quote/v1/tam/override',
    paramsSerializer: (p) =>
      standardQuerySerializer(applyQueryConfig(quoteServiceMappingsE7, p)),
  })
);
export const fetchOverrides = (params: Record<string, unknown> = {}) =>
  fetchOverridesHandler({ params });

const downloadBinderHandler = ServiceHandler({
  url: '/api/docs/v1/quote/:accountId/binder/:quoteId',
});
export function downloadBinder(accountId: any, quoteId: any, boundOn: any) {
  return downloadBinderHandler(accountId, quoteId, {
    params: { createdDate: boundOn },
    responseType: 'json',
  });
}

const rescindBinderHandler = ServiceHandler({
  url: '/api/quote/v1/rescind/:quoteId',
  method: 'put',
});
export const rescindBinder = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => rescindBinderHandler(quoteId, { data });

const toggleFollowupFlagHandler = ServiceHandler({
  url: '/api/quote/v1/P250/followup/:quoteId',
  method: 'put',
});
export const toggleFollowupFlag = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => toggleFollowupFlagHandler(quoteId, { data });

const toggleUrgentFlagHandler = ServiceHandler({
  url: '/api/quote/v1/P250/urgent/:quoteId',
  method: 'put',
});
export const toggleUrgentFlag = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => toggleUrgentFlagHandler(quoteId, { data });

const toggleSalesFlagHandler = ServiceHandler({
  url: '/api/quote/v1/P250/sales/:quoteId',
  method: 'put',
});
export const toggleSalesFlag = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => toggleSalesFlagHandler(quoteId, { data });

const setUWRiskEngHandler = ServiceHandler({
  url: '/api/quote/v1/P250/re/:quoteId',
  method: 'put',
});
export const setUWRiskEng = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => setUWRiskEngHandler(quoteId, { data });

const setUWClaimsHandler = ServiceHandler({
  url: '/api/quote/v1/P250/claims/:quoteId',
  method: 'put',
});
export const setUWClaims = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => setUWClaimsHandler(quoteId, { data });

/**
 * @name toggleRequestToBindFlag
 * @deprecated
 */
const toggleRequestToBindFlagHandler = ServiceHandler({
  url: '/api/quote/v1/P250/request-to-bind/:quoteId',
  method: 'put',
});
export const toggleRequestToBindFlag = (
  { quoteId }: { quoteId: string | number },
  data?: unknown
) => toggleRequestToBindFlagHandler(quoteId, { data });

const fetchRenewalPolicyIdHandler = ServiceHandler({
  url: '/api/quote/v1/action/renewal/:quoteId',
});
export const fetchRenewalPolicyId = ({
  quoteId,
}: {
  quoteId: string | number;
}) => fetchRenewalPolicyIdHandler(quoteId);

const checkStatusOnAccountHandler = ServiceHandler({
  url: '/api/policy/v1/business/:accountId',
});
export const checkStatusOnAccount = ({ accountId, agencyId }: any) =>
  checkStatusOnAccountHandler(accountId, { params: { agencyId } });

export const generateAutoRenewal = ServiceHandler({
  url: '/api/policy/v1/renewal/:policyId',
  method: 'POST',
});

export const pollForStatusOfAutoRenewal = ServiceHandler({
  url: '/api/quote/v1/agencyStatus/:quoteId',
});

export const existingRenewalQuoteOnAccount = ServiceHandler({
  url: '/api/quote/v1/:product/renewal-quotes/:accountId',
});

/**
 * New Issue api found in src/api/quotes.api.ts
 * @deprecated
 */
const issueRenewalPolicyForAgencyHandler = ServiceHandler({
  url: '/api/quote/v1/action/issuePolicy/:quoteId',
  method: 'PUT',
});
export const issueRenewalPolicyForAgency = (
  { quoteId, ...params }: any = {},
  data?: unknown
) =>
  issueRenewalPolicyForAgencyHandler(quoteId, {
    params: applyQueryConfig(
      [{ path: 'agreeToTerms' }, { path: 'nkllIncludedInApp' }],
      params
    ),
    data,
  });

const getSubjectivitiesForUWHandler = ServiceHandler({
  url: '/api/quote/v1/:productType/:quoteId/quote-subjectivity/settings',
});
export const getSubjectivitiesForUW = ({ productType, quoteId }: any) =>
  getSubjectivitiesForUWHandler(productType, quoteId);

const saveSubjectivitiesFromUWHandler = ServiceHandler({
  url: '/api/quote/v1/:productType/:quoteId/quote-subjectivity/settings',
  method: 'POST',
});
export const saveSubjectivitiesFromUW = (
  { productType, quoteId }: any,
  data?: unknown
) => saveSubjectivitiesFromUWHandler(productType, quoteId, { data });

const checkIfAutoRenewalQuoteExistsHandler = ServiceHandler({
  url: '/api/quote/v1/auto-renewal/:accountId/:productType',
});
export const checkIfAutoRenewalQuoteExists = ({
  accountId,
  productType,
}: any) => checkIfAutoRenewalQuoteExistsHandler(accountId, productType);

// TODO: Stubs a waive/unwaive subjectivity API
export const waiveSubjectivity = () => Promise.resolve();
